/**
 * telemetry.ts
 *
 * Thin wrapper around Application Insights that:
 *   1. Initialises the SDK once (lazy, no-op if no connection string is set).
 *   2. Attaches config.env.CORRELATION_ID to every event automatically.
 *   3. Mirrors every event/exception to the browser console so it is visible
 *      even when App Insights is unreachable.
 *
 * Performance note: All calls are fire-and-forget; none block the render path.
 * The SDK itself is loaded only when the connection string is present.
 */

import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { config } from "#/config";
// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

let _appInsights: ApplicationInsights | null = null;
let _initialised = false;

/**
 * Initialise App Insights once.  Safe to call multiple times — subsequent
 * calls are no-ops.  Should be called as early as possible (before ReactDOM).
 */
export function initTelemetry(): void {
    if (_initialised) return;
    _initialised = true;

    const connectionString =
        typeof window !== "undefined" ? config.env.APPLICATIONINSIGHTS_CONNECTION_STRING : "";

    if (!connectionString) {
        console.debug("[telemetry] No APPLICATIONINSIGHTS_CONNECTION_STRING — App Insights disabled.");
        return;
    }

    try {
        _appInsights = new ApplicationInsights({
            config: {
                connectionString,
                // Disable automatic page-load and AJAX tracking to avoid noise
                // and performance overhead — we instrument auth events manually.
                disableAjaxTracking: true,
                autoTrackPageVisitTime: false,
                enableAutoRouteTracking: false,
                disableFetchTracking: true,
                disableExceptionTracking: false, // keep uncaught JS exceptions
                maxBatchInterval: 5000, // flush at most every 5 s
            },
        });
        _appInsights.loadAppInsights();
        console.debug("[telemetry] App Insights initialised.");
    } catch (err) {
        // Non-fatal — telemetry initialisation must never break the app.
        console.warn("[telemetry] App Insights init failed:", err);
        _appInsights = null;
    }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Base properties stamped on every telemetry record. */
function _baseProps(extra?: Record<string, unknown>): Record<string, string> {
    const props: Record<string, string> = {
        correlation_id: typeof window !== "undefined" ? (config.env.CORRELATION_ID ?? "") : "",
    };
    if (extra) {
        for (const [k, v] of Object.entries(extra)) {
            props[k] = v === null || v === undefined ? "" : String(v);
        }
    }
    return props;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Track a named authentication event (success or informational).
 *
 * @param name   Short snake_case event name, e.g. `"teams_token_acquired"`.
 * @param props  Arbitrary key/value pairs — all values are coerced to strings.
 *
 * @example
 *   trackAuthEvent("msal_init_success", { account: account.username });
 */
export function trackAuthEvent(
    name: string,
    props?: Record<string, unknown>
): void {
    const merged = _baseProps(props);
    // Mirror to console
    console.info(`[telemetry][auth] ${name}`, merged);
    // Send to App Insights
    _appInsights?.trackEvent({ name }, merged);
}

/**
 * Track an authentication exception / failure.
 *
 * @param error  The caught error object.
 * @param props  Additional context such as `auth_path`, `error_code`, etc.
 */
export function trackAuthException(
    error: unknown,
    props?: Record<string, unknown>
): void {
    const merged = _baseProps(props);
    const err = error instanceof Error ? error : new Error(String(error));
    // Mirror to console
    console.error(`[telemetry][auth_error] ${err.message}`, merged);
    // Send to App Insights
    _appInsights?.trackException(
        { exception: err, severityLevel: SeverityLevel.Error },
        merged
    );
}

/**
 * Track a failed API call (4xx / 5xx).
 *
 * @param endpoint  Relative or absolute URL of the failing request.
 * @param status    HTTP status code.
 * @param props     Optional extra context (userId, tokenExpiry, etc.).
 */
export function trackApiFailure(
    endpoint: string,
    status: number,
    props?: Record<string, unknown>
): void {
    const merged = _baseProps({ endpoint, status, ...props });
    // Mirror to console
    console.warn(`[telemetry][api_failure] ${status} ${endpoint}`, merged);
    // Send to App Insights
    _appInsights?.trackEvent({ name: "api_failure" }, merged);
}
